import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Box, Map } from "lucide-react";
import { useRooms } from "@/data/rooms";
import { MaskLines, Reveal } from "@/components/MaskLines";
import { useLang } from "@/lib/i18n";

export default function RoomDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const rooms = useRooms();
  const room = rooms.find((r) => r.id === id);
  if (!room) return <Navigate to="/salas" replace />;
  const i = rooms.indexOf(room);
  const prev = rooms[(i - 1 + rooms.length) % rooms.length];
  const next = rooms[(i + 1) % rooms.length];

  return (
    <main className="mx-auto max-w-[1440px] px-5 pb-24 pt-32 md:px-10" data-testid="room-detail-page">
      <Link to="/salas" className="nav-link mono text-[11px]" data-testid="room-detail-back">← {t("bento.all")}</Link>
      <div className="mt-10 grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7" key={room.id}>
          <p className="mono text-xs" style={{ color: room.color }}>{room.num} · {room.dims} · {room.area} m²</p>
          <MaskLines as="h1" className="mt-4 font-display text-6xl leading-[0.92] sm:text-7xl lg:text-[7.5rem]" lines={[room.name, <em key="t" style={{ color: room.color }}>{room.title}</em>]} />
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-[var(--ink)]/90 md:text-lg" data-testid="room-detail-body">{room.body}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={`/explorar?mode=3d&sala=${room.id}`} className="btn-primary" data-testid="room-detail-3d"><Box size={16} /> {t("room.see3d")}</Link>
              <Link to={`/explorar?sala=${room.id}`} className="btn-ghost" data-testid="room-detail-plan"><Map size={16} /> {t("room.seePlan")}</Link>
            </div>
          </Reveal>
          <Reveal delay={0.4} className="mt-14">
            <p className="mono text-[11px] text-[var(--muted)]">{t("room.find")}</p>
            <ul className="mt-4 border-t border-white/10">
              {room.items.map((it, n) => (
                <li key={it} className="flex items-baseline gap-5 border-b border-white/10 py-4 font-display text-2xl md:text-3xl" data-testid={`room-item-${n}`}>
                  <span className="mono text-[11px] text-[var(--muted)]">0{n + 1}</span>{it}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-5">
          <div className="arch-frame aspect-[3/4] w-full border border-white/10">
            <img src={room.img} alt={room.name} className="h-full w-full object-cover" />
          </div>
        </Reveal>
      </div>
      <div className="mt-20 grid grid-cols-2 border-t border-white/10 pt-8">
        <Link to={`/salas/${prev.id}`} className="group flex items-center gap-3" data-testid="room-detail-prev">
          <ArrowLeft size={18} className="transition-transform group-hover:-translate-x-1" />
          <span className="font-display text-2xl md:text-4xl">{prev.name}</span>
        </Link>
        <Link to={`/salas/${next.id}`} className="group flex items-center justify-end gap-3 text-right" data-testid="room-detail-next">
          <span className="font-display text-2xl md:text-4xl">{next.name}</span>
          <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </main>
  );
}
